import React, { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ThicknessPicker from "../../../../../../library/UI/ThicknessPicker";
import LinePalette from "../LinePalette";
import useAnchoredPopover from "../../hooks/useAnchoredPopover";
import "./LineStyleButton.sass";

/**
 * «Colour + width» button of a line (po-chart-visual-spec.md §5.4): a colour
 * square and «1px»; it opens a popover with «Line color» (the 11 colours) and
 * «Line width» 1px…5px. Without `width` (histogram columns, backgrounds)
 * only the colour is shown and picked. Escape closes the popover only.
 *
 * @param {object}   props
 * @param {string}   props.color - CSS colour
 * @param {number|null} [props.width] - CSS px; null when the line has no width
 * @param {string[]} props.palette - engine.getLinePalette()
 * @param {(change: { color?: string, width?: number }) => void} props.onChange
 * @param {string}   props.label - the line, for the accessible name
 */
const LineStyleButton = ({ color, width = null, palette, onChange, label }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);

  const close = useCallback(() => setIsOpen(false), []);
  const popoverStyle = useAnchoredPopover({ anchorRef, popoverRef, isOpen, onClose: close });
  const hasWidth = width !== null;
  const buttonLabel = t(hasWidth ? "futures.chart.indicator_settings.line_style" : "futures.chart.indicator_settings.line_color");

  const handleKeyDown = (event) => {
    if (event.key !== "Escape" || !isOpen) return;

    // The modal around must stay open
    event.stopPropagation();
    close();
    anchorRef.current?.focus();
  };

  return (
    <span className="line-style-button" onKeyDown={handleKeyDown}>
      <button
        ref={anchorRef}
        type="button"
        className={`line-style-button__trigger${isOpen ? " line-style-button__trigger--open" : ""}`}
        aria-label={`${buttonLabel} ${label}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="line-style-button__swatch" style={{ backgroundColor: color }} aria-hidden="true" />
        {hasWidth && <span className="line-style-button__width">{`${width}px`}</span>}
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          className="line-style-button__popover"
          style={popoverStyle}
          role="dialog"
          aria-label={`${buttonLabel} ${label}`}
        >
          <p className="line-style-button__title">{t("futures.chart.indicator_settings.line_color")}</p>
          <LinePalette
            colors={palette}
            value={color}
            onChange={(nextColor) => onChange({ color: nextColor })}
            label={t("futures.chart.indicator_settings.line_color")}
          />
          {hasWidth && (
            <>
              <p className="line-style-button__title">{t("futures.chart.indicator_settings.line_width")}</p>
              <ThicknessPicker
                value={width}
                onChange={(nextWidth) => onChange({ width: nextWidth })}
                label={t("futures.chart.indicator_settings.line_width")}
              />
            </>
          )}
        </div>
      )}
    </span>
  );
};

export default LineStyleButton;
